import { useCallback, useState } from "react";
import Button from '@material-ui/core/Button';
import MapIcon from '@material-ui/icons/Public';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import { Switch, Typography, withStyles } from "@material-ui/core";
import Checkbox from '@material-ui/core/Checkbox';
import { getPerspective, isEmpty } from "../../utils/FPEUtils";
import Slider from "@material-ui/core/Slider";
import { useRecoilState, useSetRecoilState } from "recoil";
import { polarRotationState } from "../../store/PolarRotationState";
import { useMapColorState } from "../../store/MapColorState";
import { mapTimePanelVisibleState } from "../../store/MapTimePanelVisibleState";
import { useCsColorState } from "../../store/CrossSectionColorState";
import { useATCPlan } from "../../store/ATCPlanState";
import { mapPerspectiveState } from "../../store/MapPerspectiveState";
import { useMapProjectionState } from "../../store/MapProjectionState";
import { mapLegendVisibleState } from "../../store/MapLegendState";
import { csLegendVisibleState } from "../../store/CsLegendState";

function MapConfig() {
  const {atcPlan} = useATCPlan();
  const {projection, isMercator, changeMapProjection} = useMapProjectionState();
  const [polarRotation, setPolarRotation] = useRecoilState(polarRotationState);
  const [mapLegendVisible, setMapLegendVisible] = useRecoilState(mapLegendVisibleState);
  const [mapTimePanelVisible, setMapTimePanelVisible] = useRecoilState(mapTimePanelVisibleState);
  const [csLegendVisible, setCsLegendVisible] = useRecoilState(csLegendVisibleState);
  const {isDarkMap, changeMapColorDark, changeMapColorLight} = useMapColorState();
  const {isDarkCs, changeCsColorDark, changeCsColorLight} = useCsColorState();
  const setMapPerspective = useSetRecoilState(mapPerspectiveState);
  const [open, setOpen] = useState(false);

  const handleRadioChange = useCallback((event) => {
    changeMapProjection(event.target.value);

    if (!isEmpty(atcPlan.edit.json)) {
      setTimeout(_ => {
        setMapPerspective(getPerspective(atcPlan.edit.json));
      });
    }
  }, [changeMapProjection, atcPlan.edit.json, setMapPerspective]);

  const handleRotateChange = useCallback((_, value) => {
    setPolarRotation(value);
  }, [setPolarRotation]);

  const toggleMapLegend = useCallback(event => {
    setMapLegendVisible(event.target.checked);
  }, [setMapLegendVisible]);

  const toggleCsLegend = useCallback(event => {
    setCsLegendVisible(event.target.checked);
  }, [setCsLegendVisible]);

  const toggleTimePanel = useCallback(event => {
    setMapTimePanelVisible(event.target.checked);
  }, [setMapTimePanelVisible]);

  const toggleMapColor = useCallback(event => {
    if (event.target.checked) {
      changeMapColorDark();
    } else {
      changeMapColorLight();
    }
  }, [changeMapColorDark, changeMapColorLight]);

  const toggleCrossColor = useCallback(event => {
    if (event.target.checked) {
      changeCsColorDark();
    } else {
      changeCsColorLight();
    }
  }, [changeCsColorDark, changeCsColorLight]);

  const openDropdown = useCallback(event => {
    const box = event.currentTarget.parentNode.querySelector('.map-config-box');
    event.preventDefault();
    event.stopPropagation();
    if (!open) {
      setOpen(true);
      const closePreference = () => {
        box.removeEventListener('click', closePreference);
        document.removeEventListener('click', closePreference);
        setTimeout(()=>{
          setOpen(false);
        })
      };
      box.addEventListener('click', closePreference);
      document.addEventListener('click', closePreference);
    } else {
      setOpen(false);
    }
  }, [open]);

  const dropOpen = open ? 'open' : '';

  return (
    <>
      <Button aria-haspopup="true" onClick={openDropdown}>
        <div className="header-item">
          <MapIcon color={open? 'primary': 'inherit'}/>
          <span className="header-item-label">Map Config</span>
        </div>
      </Button>
      <div className={`map-config-box ${dropOpen}`}>
        <div className="dropdown-menu">
          <FormControl component="fieldset">
            <RadioGroup aria-label="projection" name="projection" value={projection} onChange={handleRadioChange}>
              <FormControlLabel value="mercator" control={<Radio />} label="Mercator" />
              <FormControlLabel value="polarNorth" control={<Radio />} label="Polar North" />
              <FormControlLabel value="polarSouth" control={<Radio />} label="Polar South" />
            </RadioGroup>
            <div hidden={isMercator}>
              <Typography>Polar Rotation</Typography>
              <PolarRotationSlider value={polarRotation} min={-180} max={180} step={10} onChange={handleRotateChange}/>
            </div>
            <FormControlLabel
              control={<Checkbox checked={mapLegendVisible} onChange={toggleMapLegend}/>}
              label="Map legend"
            />
            <FormControlLabel
              control={<Checkbox checked={mapTimePanelVisible} onChange={toggleTimePanel} />}
              label="Time Panel"
            />
            <FormControlLabel
              control={<Switch checked={isDarkMap} onChange={toggleMapColor} color="primary" />}
              label="Map Dark"
            />
            <hr width="100%"/>
            <FormControlLabel
              control={<Checkbox checked={csLegendVisible} onChange={toggleCsLegend}/>}
              label="Cross legend"
            />
            <FormControlLabel
              control={<Switch checked={isDarkCs} onChange={toggleCrossColor} color="primary" />}
              label="Cross Dark"
            />
          </FormControl>
        </div>
      </div>
    </>
  );
}

const PolarRotationSlider = withStyles(_ => ({
  root: {
    height: 4
  },
  track: {
    height: 4,
  },
  rail: {
    height: 4,
  },

}))(Slider);

export default MapConfig;